import React from 'react';
import { 
    Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, 
    Paper, TableContainer, Pagination, Button, Dialog, DialogActions, 
    DialogContent, DialogContentText, DialogTitle, TextField, FormControl, 
    InputLabel, Select, MenuItem, Snackbar, Alert, Autocomplete,
    IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { getCSAlertConfigurationList } from 'CS/Domain/Services/GetCSAlertConfigurationListService';
import { createCSAlertConfiguration } from 'CS/Domain/Services/CreateCSAlertConfigurationService';
import { deleteCSAlertConfiguration } from 'CS/Domain/Services/DeleteCSAlertConfigurationService';
import { getCSEventCodeList } from 'CS/Domain/Services/GetCSEventCodeListService';
import { getCSEventCategoryList } from 'CS/Domain/Services/GetCSEventCategoryListService';
import Loader from 'UI/Application/Components/Loaders/Loader';

function TableCSAlertConfigurationView() {
    const limit = 10;
    const [offset, setOffset] = React.useState(0);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [lastPage, setLastPage] = React.useState(1);
    const [alertConfigRows, setAlertConfigRows] = React.useState([]);
    const [alertConfigurations, setAlertConfigurations] = React.useState([]);
    const [loadAlertConfigurations, setLoadAlertConfigurations] = React.useState(true);
    
    // Add filter state variables
    const [filterType, setFilterType] = React.useState('');
    const [filterValue, setFilterValue] = React.useState('');
    
    // New state variables for creation dialog
    const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
    const [newAlertConfig, setNewAlertConfig] = React.useState({
        type: 'event_code',
        value: '',
        frequency: 24,
        threshold: 5,
        description: '',
        actionType: 'incidence'
    });
    
    // Add state variables for delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [configToDelete, setConfigToDelete] = React.useState(null);
    
    const [snackbar, setSnackbar] = React.useState({
        open: false,
        message: '',
        severity: 'success'
    });
    
    // Add event codes and categories
    const [eventCodes, setEventCodes] = React.useState([]);
    const [eventCategories, setEventCategories] = React.useState([]);
    const [loadingOptions, setLoadingOptions] = React.useState(false);
    
    // Load event codes and categories
    const loadEventCodesAndCategories = () => {
        setLoadingOptions(true);
        
        // Load event codes
        getCSEventCodeList({}).then((response) => {
            if (response !== null) {
                setEventCodes(response || []);
            }
        });
        
        // Load event categories
        getCSEventCategoryList({}).then((response) => {
            if (response !== null) {
                setEventCategories(response || []);
                setLoadingOptions(false);
            }
        });
    };
    
    // Handle opening the dialog and loading options if needed
    const handleOpenCreateDialog = () => {
        if (eventCodes.length === 0 || eventCategories.length === 0) {
            loadEventCodesAndCategories();
        }
        setOpenCreateDialog(true);
    };
    
    const handleCloseCreateDialog = () => setOpenCreateDialog(false);
    
    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Reset value when type changes
        if (name === 'type') {
            setNewAlertConfig(prev => ({
                ...prev,
                [name]: value,
                value: ''
            }));
        } else {
            setNewAlertConfig(prev => ({
                ...prev,
                [name]: name === 'frequency' || name === 'threshold' ? parseInt(value, 10) : value
            }));
        }
    };
    
    // New handler for Autocomplete change
    const handleValueChange = (event, newValue) => {
        setNewAlertConfig(prev => ({
            ...prev,
            value: newValue ? (newAlertConfig.type === 'event_code' ? newValue.event_code : newValue) : ''
        }));
    };
    
    // Handle form submission
    const handleCreateAlertConfig = () => {
        createCSAlertConfiguration(newAlertConfig)
            .then((response) => {
                if (response !== null) {
                    setSnackbar({
                        open: true,
                        message: 'Configuración de alerta creada exitosamente',
                        severity: 'success'
                    });
                    setLoadAlertConfigurations(true);
                    handleCloseCreateDialog();
                    setNewAlertConfig({
                        type: 'event_code',
                        value: '',
                        frequency: 24,
                        threshold: 5,
                        description: '',
                        actionType: 'incidence'
                    });
                } else {
                    setSnackbar({
                        open: true,
                        message: 'Error al crear la configuración de alerta',
                        severity: 'error'
                    });
                }
            });
    };
    
    // Handle delete button click
    const handleDeleteClick = (config) => {
        setConfigToDelete(config);
        setDeleteDialogOpen(true);
    };

    // Handle delete confirmation
    const handleConfirmDelete = () => {
        if (configToDelete) {
            deleteCSAlertConfiguration(configToDelete.id)
                .then((response) => {
                    if (response !== null) {
                        setSnackbar({
                            open: true,
                            message: 'Configuración de alerta eliminada exitosamente',
                            severity: 'success'
                        });
                        setLoadAlertConfigurations(true);
                    } else {
                        setSnackbar({
                            open: true,
                            message: 'Error al eliminar la configuración de alerta',
                            severity: 'error'
                        });
                    }
                });
        }
        setDeleteDialogOpen(false);
        setConfigToDelete(null);
    };

    // Handle delete dialog close
    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
        setConfigToDelete(null);
    };
    
    // Handle closing the snackbar
    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Handle filter changes
    const handleFilterTypeChange = (e) => {
        setFilterType(e.target.value);
        // Reset filter value when type changes
        setFilterValue('');
        setOffset(0);
        setCurrentPage(1);
        setLoadAlertConfigurations(true);
    };

    const handleFilterValueChange = (e) => {
        setFilterValue(e.target.value);
        setOffset(0);
        setCurrentPage(1);
        setLoadAlertConfigurations(true);
    };
    
    // New handler for filter autocomplete value changes
    const handleFilterAutocompleteChange = (event, newValue) => {
        if (filterType === 'event_code') {
            setFilterValue(newValue ? newValue.event_code : '');
        } else {
            setFilterValue(newValue || '');
        }
        setOffset(0);
        setCurrentPage(1);
        setLoadAlertConfigurations(true);
    };
    
    // Helper function to get action type label
    const getActionTypeLabel = (actionType) => {
        switch(actionType) {
            case 'incidence':
                return 'Incidencia';
            case 'alert':
                return 'Aviso';
            default:
                return actionType;
        }
    };
    
    // Helper function to get event code description
    const getEventCodeDescription = (code) => {
        const eventCode = eventCodes.find(ec => ec.event_code === code);
        return eventCode ? `${code} - ${eventCode.event_text}` : code;
    };

    React.useEffect(() => {
        if(loadAlertConfigurations){
            var data = {
                offset: offset,
                limit: limit
            };
            data = {
                ...data,
                filterType: filterType,
                filterValue: filterValue
            };

            getCSAlertConfigurationList(data).then((response) => {
                if(response !== null){
                    setAlertConfigurations(response.records);
                    setLastPage(Math.ceil(response.total / limit));
                    setLoadAlertConfigurations(false);
                    if (Math.ceil(response.total / limit) < currentPage) {
                        setCurrentPage(Math.ceil(response.total / limit));
                        if (((Math.ceil(response.total / limit) - 1) * limit) < 0) {
                          setOffset(0);
                        } else {
                          setOffset((Math.ceil(response.total / limit) - 1) * limit);
                          setLoadAlertConfigurations(true);
                        }
                    }
                }
            });
        }
    }, [loadAlertConfigurations, offset, filterType, filterValue]);

    React.useEffect(() => {
        const rows = alertConfigurations.map((config) => (
            <TableRow key={config.id}>
                <TableCell align='center'>{config.id}</TableCell>
                <TableCell align='center'>{
                    config.type === 'event_code' ? 'Código de Evento' : 'Categoría de Evento'
                }</TableCell>
                <TableCell align='center'>{
                    config.type === 'event_code' ? getEventCodeDescription(config.value) : config.value
                }</TableCell>
                <TableCell align='center'>{config.frequency}</TableCell>
                <TableCell align='center'>{config.threshold}</TableCell>
                <TableCell align='center'>{config.description || '-'}</TableCell>
                <TableCell align='center'>{getActionTypeLabel(config.actionType)}</TableCell>
                <TableCell align='center'>{config.lastActivation ? new Date(config.lastActivation).toLocaleString() : '-'}</TableCell>
                <TableCell align='center'>
                    <IconButton 
                        color="error" 
                        onClick={() => handleDeleteClick(config)}
                        title="Eliminar"
                    >
                        <DeleteIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
        ));
        setAlertConfigRows(rows);
    }, [alertConfigurations, eventCodes]);

    // Load event codes and categories when component mounts
    React.useEffect(() => {
        loadEventCodesAndCategories();
    }, []);

    // Update offset when page changes
    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
        if (((newPage - 1) * limit) < 0) {
            setOffset(0);
        } else {
            setOffset((newPage - 1) * limit);
        }
        setLoadAlertConfigurations(true);
    };

    return (
        <>
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">Configuración de Alertas CS</Typography>
                <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleOpenCreateDialog}
                >
                    Crear Nueva Alerta
                </Button>
            </Box>
            
            {/* Add filter controls */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, mb: 2 }}>
                <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                    <InputLabel id="filter-type-label">Tipo</InputLabel>
                    <Select
                        labelId="filter-type-label"
                        id="filterType"
                        value={filterType}
                        onChange={handleFilterTypeChange}
                        label="Tipo"
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="event_code">Código de Evento</MenuItem>
                        <MenuItem value="event_category">Categoría de Evento</MenuItem>
                    </Select>
                </FormControl>
                
                {filterType === '' ? (
                    <TextField
                        label="Valor"
                        variant="outlined"
                        value={filterValue}
                        onChange={handleFilterValueChange}
                    />
                ) : filterType === 'event_code' ? (
                    <Autocomplete
                        sx={{ minWidth: 250 }}
                        options={eventCodes}
                        getOptionLabel={(option) => `${option.event_code} - ${option.event_text}`}
                        value={eventCodes.find(code => code.event_code === filterValue) || null}
                        onChange={handleFilterAutocompleteChange}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Valor" 
                                variant="outlined"
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.event_code === value.event_code}
                    />
                ) : (
                    <Autocomplete
                        sx={{ minWidth: 250 }}
                        options={eventCategories}
                        value={filterValue || null}
                        onChange={handleFilterAutocompleteChange}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Valor" 
                                variant="outlined"
                            />
                        )}
                    />
                )}
            </Box>
            
            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell align='center'>ID</TableCell>
                            <TableCell align='center'>Tipo</TableCell>
                            <TableCell align='center'>Valor</TableCell>
                            <TableCell align='center'>Frecuencia</TableCell>
                            <TableCell align='center'>Umbral</TableCell>
                            <TableCell align='center'>Descripción</TableCell>
                            <TableCell align='center'>Tipo de Acción</TableCell>
                            <TableCell align='center'>Última Activación</TableCell>
                            <TableCell align='center'>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadAlertConfigurations ? (
                            <TableRow>
                                <TableCell colSpan={100} align="center">
                                    <Loader />
                                </TableCell>
                            </TableRow>
                        ) : (
                            alertConfigRows
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Pagination
                count={lastPage}
                page={currentPage}
                onChange={handlePageChange}
                sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
            />
            
            {/* Create Alert Dialog */}
            <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog}>
                <DialogTitle>Crear Nueva Configuración de Alerta</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Complete los siguientes campos para crear una nueva configuración de alerta.
                    </DialogContentText>
                    
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="tipo-label">Tipo</InputLabel>
                        <Select
                            labelId="tipo-label"
                            id="type"
                            name="type"
                            value={newAlertConfig.type}
                            label="Tipo"
                            onChange={handleInputChange}
                        >
                            <MenuItem value="event_code">Código de Evento</MenuItem>
                            <MenuItem value="event_category">Categoría de Evento</MenuItem>
                        </Select>
                    </FormControl>
                    
                    <FormControl fullWidth margin="dense">
                        {loadingOptions ? (
                            <TextField
                                fullWidth
                                label="Valor"
                                disabled
                                InputProps={{
                                    endAdornment: <Loader size={20} />
                                }}
                            />
                        ) : (
                            newAlertConfig.type === 'event_code' ? (
                                <Autocomplete
                                    id="value"
                                    options={eventCodes}
                                    getOptionLabel={(option) => `${option.event_code} - ${option.event_text}`}
                                    value={eventCodes.find(code => code.event_code === newAlertConfig.value) || null}
                                    onChange={handleValueChange}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="Valor" 
                                            fullWidth 
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.event_code === value.event_code}
                                />
                            ) : (
                                <Autocomplete
                                    id="value"
                                    options={eventCategories}
                                    value={newAlertConfig.value || null}
                                    onChange={handleValueChange}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="Valor" 
                                            fullWidth 
                                        />
                                    )}
                                />
                            )
                        )}
                    </FormControl>
                    
                    <TextField
                        margin="dense"
                        id="frequency"
                        name="frequency"
                        label="Frecuencia (horas)"
                        type="number"
                        fullWidth
                        value={newAlertConfig.frequency}
                        onChange={handleInputChange}
                    />
                    
                    <TextField
                        margin="dense"
                        id="threshold"
                        name="threshold"
                        label="Umbral"
                        type="number"
                        fullWidth
                        value={newAlertConfig.threshold}
                        onChange={handleInputChange}
                    />
                    
                    <TextField
                        margin="dense"
                        id="description"
                        name="description"
                        label="Descripción (opcional)"
                        type="text"
                        fullWidth
                        value={newAlertConfig.description}
                        onChange={handleInputChange}
                    />
                    
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="action-type-label">Tipo de Acción</InputLabel>
                        <Select
                            labelId="action-type-label"
                            id="actionType"
                            name="actionType"
                            value={newAlertConfig.actionType}
                            label="Tipo de Acción"
                            onChange={handleInputChange}
                        >
                            <MenuItem value="incidence">Incidencia</MenuItem>
                            <MenuItem value="alert">Aviso</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreateDialog} color="primary">Cancelar</Button>
                    <Button 
                        onClick={handleCreateAlertConfig} 
                        color="primary"
                        disabled={!newAlertConfig.value || !newAlertConfig.frequency || !newAlertConfig.threshold}
                    >
                        Crear
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
            >
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro que desea eliminar esta configuración de alerta?
                        Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Notification Snackbar */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
        </>
    );
}

export default TableCSAlertConfigurationView;
